import { AuthEnums } from "models/enums";
import { WintSuperkollAPIModel, WintSuperkollAPIModelAnnualAccounts } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { TagsProps } from "models/propTypes/commonPropTypes";
//#endregion
import useAPIClientHelper from "./useAPIClientHelper";
import { FileHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

// Only individual types are mapped here. Combined types (AllAnnualDocuments, AnnualReportAndAnnualGeneralMeeting, etc.)
// are split into individual attachments by GetDocumentsMeta on the backend, so each attachment always has an individual imageType.
export const imageTypeToStatementType: Record<number, WintSuperkollAPIModel.FinancialStatementType> = {
  [WintSuperkollAPIModel.ImageType.AnnualReport]: WintSuperkollAPIModel.FinancialStatementType.AnnualReport,
  [WintSuperkollAPIModel.ImageType.IncomeTaxReturn]: WintSuperkollAPIModel.FinancialStatementType.IncomeTaxReturn,
  [WintSuperkollAPIModel.ImageType.AnnualGeneralMeeting]:
    WintSuperkollAPIModel.FinancialStatementType.AnnualGeneralMeeting,
};

function useAnnualAccountService() {
  const { apiClientInstance } = useAPIClientHelper();

  const approveAnnualAccountReport = (
    id: number,
    request: WintSuperkollAPIModelAnnualAccounts.AnnualAccountsApproveRequest = {}
  ) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).AnnualAccountsClient.Approve(
      id,
      request
    );
  };

  const rejectAnnualAccountReport = (id: number, message?: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .AnnualAccountsClient.Reject(id, {
        comment: message,
      } as WintSuperkollAPIModelAnnualAccounts.AnnualAccountsRejectRequest)
      .then((res) => res.data);
  };

  const commentAnnualAccountReport = (id: number, message?: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .AnnualAccountsClient.Comment(id, {
        comment: message,
      } as WintSuperkollAPIModelAnnualAccounts.AnnualAccountsCommentRequest)
      .then((res) => res.data);
  };

  const orderK10AnnualAccountReport = async (id: number, message: string) => {
    const res = await apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).AnnualAccountsClient.OrderK10(id, {
      comment: message,
    } as WintSuperkollAPIModelAnnualAccounts.OrderK10Request);
    return res.data;
  };

  const downloadPdf = async (id: number, reportType: WintSuperkollAPIModel.FinancialStatementType) => {
    if (reportType === WintSuperkollAPIModel.FinancialStatementType.Questionnaire) {
      return;
    }

    return apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    })
      .AnnualAccountsClient.DownloadPdf(id, reportType)
      .then((res) => {
        FileHelper.download(res);
      });
  };

  const getFinancialStatement = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company).AnnualAccountsClient.GetAnnualReport(id);
  };

  const getFinancialStatements = () => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .AnnualAccountsClient.GetFinancialStatements()
      .then((res) => res.data);
  };

  const getTodoState = (): TagsProps => {
    return {
      tags: [LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ToApprove)],
      className: cssExports.yellowTag,
    };
  };

  const confirmAuditorReportUploaded = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).AnnualAccountsClient.ConfirmAuditorReportUploaded(id);
  };

  const setAnnualReportToHandledByAuditor = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).AnnualAccountsClient.SetAnnualReportToHandledByAuditor(id);
  };

  const confirmAuditorReportSigned = (id: number, financialStatementsApprovalSigneePersonId?: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).AnnualAccountsClient.ConfirmedAuditorReportSigned(id, { financialStatementsApprovalSigneePersonId });
  };

  const getFinancialStatementsApprovalSignees = async (financialYearId: number) => {
    const res = await apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).AnnualAccountsClient.GetFinancialStatementsApprovalSignees(financialYearId);
    return res.data;
  };

  const getDocumentsMeta = (financialYearId: number, reportType: WintSuperkollAPIModel.FinancialStatementType) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .AnnualAccountsClient.GetDocumentsMeta(financialYearId, reportType)
      .then((res) => res.data);
  };

  const getDocumentPdf = (financialYearId: number, reportType: WintSuperkollAPIModel.FinancialStatementType) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .AnnualAccountsClient.GetDocumentPdf(financialYearId, reportType)
      .then((res) => res.data.data ?? "");
  };

  const getDocumentFile = (financialYearId: number, imageType?: WintSuperkollAPIModel.ImageType) => {
    if (imageType === undefined) return Promise.resolve("");

    const financialStatementType = imageTypeToStatementType[imageType];
    if (financialStatementType === undefined) return Promise.resolve("");

    return getDocumentPdf(financialYearId, financialStatementType);
  };

  const getDocumentPreview = (
    financialYearId: number,
    reportType: WintSuperkollAPIModel.FinancialStatementType,
    dividend?: number,
    signal?: AbortSignal
  ): Promise<string> => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .AnnualAccountsClient.GetDocumentPdf(financialYearId, reportType, true, dividend, { signal })
      .then((res) => res.data.data ?? "");
  };

  const dowloadTaxCalculationReport = async (financialYearId: number) => {
    const result = await apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    }).AnnualAccountsClient.DownloadTaxCalculationReportForFinancialYear(financialYearId);
    FileHelper.download(result);
  };

  const getAnnualReportTimeline = (financialYearId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company).AnnualAccountsClient.GetAnnualReportTimeLine(financialYearId);
  };

  const getDividendInformation = async (financialYearId: number) => {
    const res = await apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).AnnualAccountsClient.GetDividendsInfoAsync(financialYearId);
    return res.data;
  };

  const getAuditorAssistants = async (financialYearId: number) => {
    const res = await apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).AnnualAccountsClient.GetAuditorAssistants(financialYearId);
    return res.data;
  };

  const updateAuditorAssistants = async (financialYearId: number, socialSecurityNumbers: string[]) => {
    // showErrors: false — the modal's catch handler renders the error toast with assistant-specific
    // wording; the global interceptor would otherwise toast a duplicate generic message.
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: false,
    }).AnnualAccountsClient.UpdateAuditorAssistants(financialYearId, { socialSecurityNumbers });
  };

  return {
    approveAnnualAccountReport,
    rejectAnnualAccountReport,
    commentAnnualAccountReport,
    orderK10AnnualAccountReport,
    downloadPdf,
    getFinancialStatement,
    getFinancialStatements,
    getTodoState,
    confirmAuditorReportUploaded,
    setAnnualReportToHandledByAuditor,
    confirmAuditorReportSigned,
    getFinancialStatementsApprovalSignees,
    getDocumentsMeta,
    getDocumentPdf,
    getDocumentFile,
    getDocumentPreview,
    dowloadTaxCalculationReport,
    getAnnualReportTimeline,
    getDividendInformation,
    getAuditorAssistants,
    updateAuditorAssistants,
  };
}

export default useAnnualAccountService;
